import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
  EllipsisVerticalIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  ArchiveBoxIcon,
  PaperAirplaneIcon,
  LinkIcon,
  UserGroupIcon,
  ChartBarIcon,
  EyeIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { useDiagnosticsStore, type DiagnosticFormWithMeta } from '../stores/diagnosticsStore';
import { useCanUseFeature } from '../stores/subscriptionStore';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { Modal } from '../components/ui/Modal';
import { PortalSettingsModal } from '../components/diagnostics/PortalSettingsModal';
import { AssociateClientModal } from '../components/diagnostics/AssociateClientModal';
import { formatRelativeTime } from '../utils/dateFormat';
import clsx from 'clsx';
import type { DiagnosticFramework, FormStatus } from '@affogato/shared';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.03 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.15 } },
};

function getStatusColor(status: FormStatus) {
  switch (status) {
    case 'published':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'archived':
      return 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400';
    default:
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
  }
}

function getFrameworkColor(framework: DiagnosticFramework) {
  switch (framework) {
    case 'togaf10':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'cobit2019':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400';
  }
}

export function DiagnosticsPage() {
  const { t } = useTranslation('diagnostics');
  const navigate = useNavigate();
  const {
    forms,
    isLoading,
    templates,
    fetchForms,
    fetchTemplates,
    createForm,
    createFromTemplate,
    generateFormWithAI,
    duplicateForm,
    deleteForm,
    publishForm,
    archiveForm,
    associateToClient,
  } = useDiagnosticsStore();

  const diagQuota = useCanUseFeature('diagnostics');
  const aiQuota = useCanUseFeature('ai_uses');

  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createMode, setCreateMode] = useState<'template' | 'scratch' | 'ai'>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiError, setAiError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [portalForm, setPortalForm] = useState<DiagnosticFormWithMeta | null>(null);
  const [associateForm, setAssociateForm] = useState<DiagnosticFormWithMeta | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchForms();
    fetchTemplates();
  }, [fetchForms, fetchTemplates]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClick = () => {
      setActiveMenu(null);
      setContextMenuPosition(null);
    };
    if (activeMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [activeMenu]);

  // Adjust context menu position to stay within viewport
  useEffect(() => {
    if (contextMenuRef.current && contextMenuPosition) {
      const rect = contextMenuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = contextMenuPosition.x;
      let adjustedY = contextMenuPosition.y;

      if (rect.right > viewportWidth) {
        adjustedX = contextMenuPosition.x - rect.width;
      }
      if (rect.bottom > viewportHeight) {
        adjustedY = contextMenuPosition.y - rect.height;
      }

      if (adjustedX !== contextMenuPosition.x || adjustedY !== contextMenuPosition.y) {
        setContextMenuPosition({ x: adjustedX, y: adjustedY });
      }
    }
  }, [contextMenuPosition]);

  // Handle right-click context menu
  const handleContextMenu = (e: React.MouseEvent, formId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveMenu(formId);
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
  };

  const filteredForms = forms.filter((form) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      form.title.toLowerCase().includes(query) ||
      form.description?.toLowerCase().includes(query)
    );
  });

  const handleCreate = async () => {
    // Check diagnostics quota for all creation modes
    if (!diagQuota.allowed) {
      setAiError(`Has alcanzado el límite de ${diagQuota.limit} diagnósticos de tu plan. Actualiza tu plan para continuar.`);
      return;
    }

    // For AI mode, also check AI quota
    if (createMode === 'ai' && !aiQuota.allowed) {
      setAiError(`Has alcanzado el límite de ${aiQuota.limit} solicitudes de IA de tu plan. Actualiza tu plan para continuar.`);
      return;
    }

    if (createMode === 'template' && selectedTemplate) {
      setIsCreating(true);
      try {
        const form = await createFromTemplate(selectedTemplate);
        setIsCreateModalOpen(false);
        resetCreateForm();
        navigate(`/diagnostics/${form.id}`);
      } catch (error) {
        console.error('Error creating from template:', error);
      } finally {
        setIsCreating(false);
      }
    } else if (createMode === 'scratch' && formTitle) {
      setIsCreating(true);
      try {
        const form = await createForm({
          title: formTitle,
          description: formDescription || undefined,
          framework: 'custom',
          structure: {
            sections: [
              {
                id: crypto.randomUUID(),
                title: t('builder.sectionTitle'),
                order: 0,
                questions: [],
              },
            ],
            settings: {
              showProgressBar: true,
              allowSaveProgress: true,
              randomizeQuestions: false,
              showSectionTitles: true,
            },
          },
        });
        setIsCreateModalOpen(false);
        resetCreateForm();
        navigate(`/diagnostics/${form.id}`);
      } catch (error) {
        console.error('Error creating form:', error);
      } finally {
        setIsCreating(false);
      }
    } else if (createMode === 'ai' && aiPrompt.trim().length >= 10) {
      setIsCreating(true);
      setAiError(null);
      try {
        const form = await generateFormWithAI({
          content: aiPrompt,
          framework: 'custom',
          locale: 'es',
        });
        setIsCreateModalOpen(false);
        resetCreateForm();
        navigate(`/diagnostics/${form.id}`);
      } catch (error) {
        console.error('Error generating form with AI:', error);
        if (error instanceof Error) {
          const msg = error.message;
          if (msg.includes('AI_NOT_CONFIGURED')) {
            setAiError(t('create.aiNotConfigured') || 'La IA no está configurada en el servidor');
          } else if (msg.includes('AI_GENERATION_ERROR')) {
            // Extract the specific error from the server
            const specificError = msg.replace('AI_GENERATION_ERROR:', '').trim();
            setAiError(`Error de IA: ${specificError}`);
          } else if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
            setAiError('Error de conexión. Verifica tu internet e intenta de nuevo.');
          } else {
            setAiError(t('create.aiError') || 'Error al generar el formulario. Intenta de nuevo.');
          }
        } else {
          setAiError(t('create.aiError') || 'Error al generar el formulario. Intenta de nuevo.');
        }
      } finally {
        setIsCreating(false);
      }
    }
  };

  const resetCreateForm = () => {
    setCreateMode('template');
    setSelectedTemplate(null);
    setFormTitle('');
    setFormDescription('');
    setAiPrompt('');
    setAiError(null);
  };

  const handleDuplicate = async (id: string) => {
    try {
      const form = await duplicateForm(id);
      navigate(`/diagnostics/${form.id}`);
    } catch (error) {
      console.error('Error duplicating form:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteForm(id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting form:', error);
    }
  };

  const handlePublish = async (id: string) => {
    try {
      await publishForm(id);
    } catch (error) {
      console.error('Error publishing form:', error);
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await archiveForm(id);
    } catch (error) {
      console.error('Error archiving form:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('title')}</h1>
          <p className="text-gray-500 dark:text-slate-400 mt-1">{t('subtitle')}</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <PlusIcon className="w-5 h-5" />
          {t('createForm')}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder={t('searchPlaceholder') || 'Search forms...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : filteredForms.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center justify-center py-20 text-center"
        >
          <div className="w-20 h-20 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
            <ClipboardDocumentListIcon className="w-10 h-10 text-gray-400 dark:text-slate-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            {searchQuery ? t('empty.searchTitle') || 'No forms found' : t('empty.title')}
          </h3>
          <p className="text-gray-500 dark:text-slate-400 max-w-sm mb-6">
            {searchQuery
              ? t('empty.searchDescription') || 'Try a different search term'
              : t('empty.description')}
          </p>
          {!searchQuery && (
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <PlusIcon className="w-5 h-5" />
              {t('empty.create')}
            </Button>
          )}
        </motion.div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-1"
        >
          {filteredForms.map((form) => (
            <motion.div
              key={form.id}
              variants={itemVariants}
              onClick={() => navigate(`/diagnostics/${form.id}`)}
              onContextMenu={(e) => handleContextMenu(e, form.id)}
              className="flex items-center gap-4 px-4 py-3 rounded-xl cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-slate-800 group"
            >
              {/* Icon */}
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-primary-100 dark:bg-primary-900/30">
                <ClipboardDocumentListIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>

              {/* Title and description */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate">{form.title}</p>
                <p className="text-sm text-gray-500 dark:text-slate-400 truncate">
                  {form.structure?.sections?.length || 0} {t('card.sections')} • {form.structure?.sections?.reduce((acc, s) => acc + (s.questions?.length || 0), 0) || 0} {t('card.questions')}
                  {form.responseCount !== undefined && form.responseCount > 0 && (
                    <span className="text-primary-600 dark:text-primary-400 ml-2">
                      • {form.responseCount} {t('card.responses')}
                    </span>
                  )}
                </p>
              </div>

              {/* Status badge */}
              <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0', getStatusColor(form.status))}>
                {t(`status.${form.status}`)}
              </span>

              {/* Framework badge */}
              <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 hidden sm:inline-flex', getFrameworkColor(form.framework))}>
                {t(`frameworks.${form.framework}`)}
              </span>

              {/* Date */}
              <span className="text-sm text-gray-400 dark:text-slate-500 flex-shrink-0 hidden md:block w-24 text-right">
                {formatRelativeTime(form.updatedAt)}
              </span>

              {/* Menu button */}
              <div className="relative flex-shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setContextMenuPosition(null); // Reset context position for button click
                    setActiveMenu(activeMenu === form.id ? null : form.id);
                  }}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <EllipsisVerticalIcon className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                </button>

                {/* Dropdown Menu */}
                {activeMenu === form.id && (
                  <div
                    ref={contextMenuRef}
                    className={clsx(
                      'w-48 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 py-1 z-50',
                      contextMenuPosition ? 'fixed' : 'absolute right-0 top-8'
                    )}
                    style={contextMenuPosition ? { left: contextMenuPosition.x, top: contextMenuPosition.y } : undefined}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/diagnostics/${form.id}`);
                        setActiveMenu(null);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                    >
                      <PencilIcon className="w-4 h-4" />
                      {t('card.edit')}
                    </button>
                    {form.responseCount !== undefined && form.responseCount > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/diagnostics/${form.id}/responses`);
                          setActiveMenu(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        <EyeIcon className="w-4 h-4" />
                        {t('card.viewResponses')}
                      </button>
                    )}
                    {form.responseCount !== undefined && form.responseCount > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/diagnostics/${form.id}/analysis`);
                          setActiveMenu(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        <ChartBarIcon className="w-4 h-4" />
                        {t('card.viewAnalysis')}
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(form.id);
                        setActiveMenu(null);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                      {t('card.duplicate')}
                    </button>
                    {form.status === 'published' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPortalForm(form);
                          setActiveMenu(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        <LinkIcon className="w-4 h-4" />
                        {t('card.sharePortal')}
                      </button>
                    )}
                    {!form.clientId && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setAssociateForm(form);
                          setActiveMenu(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        <UserGroupIcon className="w-4 h-4" />
                        {t('card.associateClient')}
                      </button>
                    )}
                    <div className="border-t border-gray-200 dark:border-slate-700 my-1" />
                    {form.status === 'draft' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePublish(form.id);
                          setActiveMenu(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        <PaperAirplaneIcon className="w-4 h-4" />
                        {t('card.publish')}
                      </button>
                    )}
                    {form.status !== 'archived' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchive(form.id);
                          setActiveMenu(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        <ArchiveBoxIcon className="w-4 h-4" />
                        {t('card.archive')}
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(form.id);
                        setActiveMenu(null);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <TrashIcon className="w-4 h-4" />
                      {t('card.delete')}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Create Form Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          resetCreateForm();
        }}
        title={t('create.title')}
      >
        <div className="space-y-4">
          {/* Mode Selection */}
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setCreateMode('template')}
              className={clsx(
                'p-3 rounded-lg border-2 transition-all text-left',
                createMode === 'template'
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
              )}
            >
              <div className="font-medium text-sm text-gray-900 dark:text-white">
                {t('create.fromTemplate')}
              </div>
              <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                {t('create.templateDescription')}
              </div>
            </button>
            <button
              onClick={() => setCreateMode('scratch')}
              className={clsx(
                'p-3 rounded-lg border-2 transition-all text-left',
                createMode === 'scratch'
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
              )}
            >
              <div className="font-medium text-sm text-gray-900 dark:text-white">
                {t('create.fromScratch')}
              </div>
              <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                {t('create.scratchDescription')}
              </div>
            </button>
            <button
              onClick={() => setCreateMode('ai')}
              className={clsx(
                'p-3 rounded-lg border-2 transition-all text-left',
                createMode === 'ai'
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
              )}
            >
              <div className="font-medium text-sm text-gray-900 dark:text-white">
                {t('create.withAI')}
              </div>
              <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                {t('create.aiDescription')}
              </div>
            </button>
          </div>

          {/* Template Selection */}
          {createMode === 'template' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                {t('create.selectTemplate')}
              </label>
              <div className="space-y-2">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={clsx(
                      'w-full p-3 rounded-lg border-2 transition-all text-left',
                      selectedTemplate === template.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                    )}
                  >
                    <div className="font-medium text-gray-900 dark:text-white">
                      {template.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
                      {template.description}
                    </div>
                    <div className="flex gap-3 mt-2 text-xs text-gray-400 dark:text-slate-500">
                      <span>{template.sectionCount} {t('card.sections')}</span>
                      <span>{template.questionCount} {t('card.questions')}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* From Scratch Form */}
          {createMode === 'scratch' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  {t('create.formTitle')}
                </label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white"
                  placeholder={t('create.formTitle')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  {t('create.formDescription')}
                </label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white resize-none"
                  placeholder={t('create.formDescription')}
                />
              </div>
            </div>
          )}

          {/* AI Generation */}
          {createMode === 'ai' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  {t('create.aiInstructions')}
                </label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => {
                    setAiPrompt(e.target.value);
                    if (aiError) setAiError(null);
                  }}
                  rows={5}
                  readOnly={isCreating}
                  className={`w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white resize-none ${isCreating ? 'cursor-not-allowed bg-gray-50 dark:bg-slate-900' : ''}`}
                  placeholder={t('create.aiPlaceholder')}
                />
                <div className="flex justify-between mt-1">
                  <span className={`text-xs ${aiPrompt.trim().length < 10 ? 'text-amber-500' : 'text-gray-400 dark:text-slate-500'}`}>
                    {aiPrompt.trim().length < 10
                      ? `${t('create.aiMinChars') || 'Mínimo 10 caracteres'} (${aiPrompt.trim().length}/10)`
                      : `${aiPrompt.trim().length} ${t('create.aiChars') || 'caracteres'}`
                    }
                  </span>
                </div>
              </div>
              {aiError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{aiError}</p>
                </div>
              )}
              {isCreating && createMode === 'ai' && (
                <div className="p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Spinner size="sm" />
                    <div>
                      <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
                        {t('create.aiGenerating') || 'Generando formulario con IA...'}
                      </p>
                      <p className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
                        {t('create.aiGeneratingHint') || 'Esto puede tomar unos segundos'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {!isCreating && (
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {t('create.aiDescription')}
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
            <Button
              variant="ghost"
              onClick={() => {
                setIsCreateModalOpen(false);
                resetCreateForm();
              }}
            >
              {t('create.cancel')}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={
                isCreating ||
                (createMode === 'template' && !selectedTemplate) ||
                (createMode === 'scratch' && !formTitle) ||
                (createMode === 'ai' && aiPrompt.trim().length < 10)
              }
            >
              {isCreating ? <Spinner size="sm" /> : t('create.create')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title={t('card.delete')}
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-slate-400">
            {t('card.confirmDelete')}
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
              {t('create.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            >
              {t('card.delete')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Portal Settings Modal */}
      {portalForm && (
        <PortalSettingsModal
          isOpen={!!portalForm}
          onClose={() => setPortalForm(null)}
          formId={portalForm.id}
          portal={portalForm.portal}
        />
      )}

      {/* Associate Client Modal */}
      {associateForm && (
        <AssociateClientModal
          isOpen={!!associateForm}
          onClose={() => setAssociateForm(null)}
          formTitle={associateForm.title}
          onAssociate={async (clientId) => {
            await associateToClient(associateForm.id, clientId);
            await fetchForms();
          }}
        />
      )}
    </div>
  );
}
